/**
 * `/flows/:flowId` — the builder, full screen. Slice 69.
 *
 * This file only gathers what the editor needs and hands it the real API; the
 * editor itself is `components/flow/FlowEditor.tsx`. It reads, in parallel:
 *
 *   - the flow (`GET /flows/:id`) — versions, the draft, the live graph;
 *   - the segments a trigger or an audience may name (rule segments marked);
 *   - the APPROVED email and WhatsApp templates — the only ones a message step
 *     may use — and how many others exist, so the add menu can say they are
 *     hidden rather than leave someone hunting for a pending template;
 *   - the field catalogue (conditions, variable sources), null when the API
 *     does not serve it yet, which the condition form says in words;
 *   - the declared outcome types, for a Goal step. A workspace without Sense
 *     has none, which is an honest empty list, not a failure.
 *
 * A WhatsApp template's bubble and quick replies need its document, so the
 * editor asks for each template's shape as a step first needs it
 * (`loadWaShape`), not all of them up front.
 *
 * It renders outside `AppLayout` on purpose: the reference editor is a
 * full-bleed canvas with its own top bar and a "Close editor" that returns to
 * the list, and a sidebar beside a pannable canvas leaves too little canvas.
 */
import {
  requiredWaPositionsForDocument,
  type FlowDetail,
  type OutcomeType,
  type TemplateSummary,
} from '@wend/shared';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorState } from '../../components/ErrorState';
import { FullPageLoader } from '../../components/FullPageLoader';
import { FlowEditor, type FlowEditorApi } from '../../components/flow/FlowEditor';
import type { FlowEditorData, FlowTemplateOption, WaTemplateShape } from '../../components/flow/model';
import {
  archiveFlow,
  enrolFlow,
  fetchFieldCatalogueForFlow,
  fetchFlow,
  fetchFlowEnrollment,
  fetchFlowStats,
  fetchFlowTestPeople,
  launchFlow,
  listFlowEnrollments,
  pauseFlow,
  preflightFlow,
  resumeFlow,
  saveFlowDraft,
  suggestFlowStep,
  testFlow,
} from '../../lib/api/flows';
import { fetchOutcomes } from '../../lib/api/sense';
import { listSegments } from '../../lib/api/segments';
import { fetchTemplate, listTemplates } from '../../lib/api/templates';
import { useAsyncData } from '../../lib/use-async-data';

interface Loaded {
  flow: FlowDetail;
  data: FlowEditorData;
  hiddenTemplates: number;
}

async function allTemplates(type: 'email' | 'whatsapp', signal: AbortSignal): Promise<TemplateSummary[]> {
  const page = await listTemplates({ page: 1, pageSize: 100, type, sort: 'name', direction: 'asc' }, signal);
  return page.items;
}

async function load(flowId: string, signal: AbortSignal): Promise<Loaded> {
  const [flow, segments, wa, email, catalogue, outcomeTypes] = await Promise.all([
    fetchFlow(flowId, signal),
    listSegments({ page: 1, pageSize: 100, sort: 'name', direction: 'asc' }, signal),
    allTemplates('whatsapp', signal),
    allTemplates('email', signal),
    fetchFieldCatalogueForFlow(signal),
    fetchOutcomes(signal).then(
      (overview) => overview.types as OutcomeType[],
      () => [] as OutcomeType[],
    ),
  ]);
  const all = [...wa, ...email];
  const approved = all.filter((t) => t.status === 'approved');
  const templates: FlowTemplateOption[] = approved.map((t) => ({
    id: t.id,
    name: t.name,
    channel: t.type === 'email' ? 'email' : 'whatsapp',
    subject: t.subject,
  }));
  return {
    flow,
    data: { segments: segments.items, templates, waShapes: new Map(), fields: catalogue?.fields ?? null, outcomeTypes },
    hiddenTemplates: all.length - approved.length,
  };
}

/** A WhatsApp template's document, as the canvas draws it. Null when it is not a WhatsApp template. */
async function loadWaShape(templateId: string): Promise<WaTemplateShape | null> {
  try {
    const template = await fetchTemplate(templateId);
    const doc = template.whatsapp;
    if (!doc) return null;
    return {
      header: doc.header?.format === 'text' ? doc.header.text : null,
      body: doc.body,
      footer: doc.footer,
      buttons: doc.buttons.flatMap((button) => (button.type === 'quick_reply' ? [button.text] : [])),
      positions: requiredWaPositionsForDocument(doc),
    };
  } catch {
    return null;
  }
}

export function FlowEditorPage() {
  const { flowId = '' } = useParams<{ flowId: string }>();
  const navigate = useNavigate();
  const loaded = useAsyncData((signal) => load(flowId, signal), [flowId]);

  const api = useMemo<FlowEditorApi>(
    () => ({
      save: saveFlowDraft,
      preflight: preflightFlow,
      launch: launchFlow,
      pause: pauseFlow,
      resume: resumeFlow,
      archive: archiveFlow,
      enrol: enrolFlow,
      suggest: suggestFlowStep,
      test: testFlow,
      testPeople: fetchFlowTestPeople,
      stats: fetchFlowStats,
      loadWaShape,
      enrollments: listFlowEnrollments,
      enrollment: fetchFlowEnrollment,
    }),
    [],
  );

  if (loaded.status === 'error' && loaded.data === null) {
    return (
      <main className="grid min-h-dvh place-items-center bg-surface-1 p-6">
        <ErrorState
          error={loaded.error}
          title="Could not open this flow"
          onRetry={loaded.reload}
          className="flex max-w-md flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
        />
      </main>
    );
  }
  if (loaded.data === null) return <FullPageLoader label="Opening the flow" />;

  return (
    <FlowEditor
      key={loaded.data.flow.id}
      flow={loaded.data.flow}
      data={loaded.data.data}
      hiddenTemplates={loaded.data.hiddenTemplates}
      api={api}
      onClose={() => navigate('/flows')}
    />
  );
}
